import {
  Button,
  Icon,
  Input,
  Menu,
  Pagination,
  Popup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from 'semantic-ui-react';
import {getFileLabelFromType, getFileSizeFormatted} from '../utils/LabelUtils';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  RepositoryFileRead,
  useApiAdminV1RepositoryRepositoryFilesDestroyMutation,
} from '../../../../store/generated/admin/repository-files';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../store/store';
import {setTypeFilter} from '../repositoryManagementSlice';
import {DeleteConfirmationModal} from './DeleteConfirmationModal';
import {useToasts} from 'common/toast';
import {FileTypes, getFileTypeIcon} from 'common/constants/filetypes';
import { openResourceLink } from 'common/utils/resourceLinks';
import {ControlContainer, PageHeader, PageSection} from 'common/styles/page';
import {StyledAddButton, StyledTrashButton} from 'common/components/button/StyledButton';
import {FileViewModal} from './FileViewModal';
import {ProcessingStatus, TranscriptionStatus} from './ProcessingStatusIndicator';

type RepositoryFileWithVotes = RepositoryFileRead & {
  upvote_count?: number;
  downvote_count?: number;
};

type RepositoryContentListProps = {
  data: RepositoryFileRead[] | { results?: RepositoryFileRead[] } | undefined;
  editable: boolean;
  onRowClick: (repositoryFile: RepositoryFileRead) => void;
};

const RepositoryContentList = (props: RepositoryContentListProps) => {
  const { data } = props;
  const typedData = Array.isArray(data)
    ? (data as RepositoryFileWithVotes[])
    : Array.isArray((data as { results?: RepositoryFileRead[] })?.results)
      ? ((data as { results?: RepositoryFileRead[] }).results as RepositoryFileWithVotes[])
      : ([] as RepositoryFileWithVotes[]);

  const navigate = useNavigate();
  const { addToast } = useToasts();
  const dispatch = useDispatch();
  const persistedTypeFilter = useSelector((state: RootState) => state.repositoryManagement.typeFilter);
  const ALL_FILE_TYPES = '-1';

  const [pendingDeleteFileId, setPendingDeleteFileId] = React.useState<number | undefined>(
    undefined
  );
  const [viewFileId, setViewFileId] = React.useState<number>(0);
  const [filterState, setFilterState] = useState<{ [key: string]: string }>({
    name: '',
    type: persistedTypeFilter,
  });
  const [activePage, setActivePage] = useState(1);
  const [sortProperty, setSortProperty] = useState('name');
  const [sortDirection, setSortDirection] = useState(1);
  const itemsPerPage = Math.floor((window.innerHeight - 264) / (props.editable ? 90 : 100));
  const [pendingFilterState, setPendingFilterState] = useState(filterState);
  const pendingDelete = useRef<number | undefined>();
  const [isFileViewOpen, setFileViewOpen] = React.useState(false);
  const [viewFileName, setViewFileName] = useState<string>('');

  const [triggerDelete, deleteStatus] = useApiAdminV1RepositoryRepositoryFilesDestroyMutation();

  const getProcessingStatusIcon = (file: any) => {
    const processingStatus = file.processing_status as ProcessingStatus;
    const transcriptionStatus = file.transcription_status as TranscriptionStatus;
    const isMP4 = file.type === FileTypes.MP4;

    if (processingStatus === ProcessingStatus.PROCESSING) {
      return <Icon name="spinner" loading color="blue" size="small" title="Processing content" />;
    }
    if (processingStatus === ProcessingStatus.FAILED) {
      return (
        <Icon name="exclamation triangle" color="red" size="small" title="Processing failed" />
      );
    }
    if (processingStatus === ProcessingStatus.COMPLETED) {
      return (
        <Icon name="check circle" color="green" size="small" title="Processing complete" />
      );
    }
    if (isMP4 && transcriptionStatus === TranscriptionStatus.IN_PROGRESS) {
      return <Icon name="spinner" loading color="blue" size="small" title="Transcribing audio" />;
    }
    if (isMP4 && transcriptionStatus === TranscriptionStatus.FAILED) {
      return (
        <Icon name="exclamation triangle" color="red" size="small" title="Transcription failed" />
      );
    }
    return null;
  };

  const getMissingResourceDataFields = (file: RepositoryFileRead) => {
    const missing: string[] = [];
    const hasValue = (value?: string | null) => typeof value === 'string' && value.trim().length > 0;

    if (!hasValue(file.name)) {
      missing.push('Name');
    }
    if (!hasValue(file.description)) {
      missing.push('Description');
    }
    if (!hasValue(file.source)) {
      missing.push('Source');
    }
    if (!hasValue(file.tags)) {
      missing.push('Themes');
    }
    if (!hasValue(file.outcomes)) {
      missing.push('Outcomes');
    }
    if (!hasValue(file.reference)) {
      missing.push('Reference');
    }

    return missing;
  };

  const getDataStatusIcon = (file: RepositoryFileRead) => {
    const missingFields = getMissingResourceDataFields(file);
    const hasIssues = missingFields.length > 0;

    return (
      <Popup
        trigger={
          <Icon
            name={hasIssues ? 'warning sign' : 'check circle'}
            color={hasIssues ? 'yellow' : 'green'}
            title={hasIssues ? 'Missing required data fields' : 'All required data fields present'}
          />
        }
        content={
          hasIssues ? (
            <div>
              {missingFields.map((field) => (
                <div key={field}>{field}</div>
              ))}
            </div>
          ) : (
            'All required data fields are present.'
          )
        }
        header="Data completeness"
        position="top center"
        inverted
      />
    );
  };

  const handleDelete = (fileId?: number | undefined) => {
    if (!fileId) {
      console.warn('No file id provided');
      return;
    }
    triggerDelete({ id: fileId })
      .unwrap()
      .then(() => {
        addToast('Successfully deleted file information', {
          appearance: 'success',
        });
      })
      .catch(() => {
        addToast('There was an error deleting file information', {
          appearance: 'error',
        });
      });

    setPendingDeleteFileId(undefined);
  };

  useEffect(() => {
    if (pendingDelete.current && !deleteStatus.isLoading) {
      if (deleteStatus.isSuccess) {
        addToast('File deleted', { appearance: 'success' });
      } else if (deleteStatus.isError) {
        addToast('Failed to delete file', {
          appearance: 'error',
          autoDismiss: false,
          key: 'unique-toast',
        });
      }
      pendingDelete.current = undefined;
    }
  }, [addToast, deleteStatus]);

  const confirmDelete = (fileId: number) => {
    setPendingDeleteFileId(fileId);
  };

  const handleViewClick = (
    fileId: number,
    fileType: number,
    object_key: string,
    fileName: string
  ) => {
    openResourceLink(
      { type: fileType, object_key, id: fileId, name: fileName },
      {
        openInApp: () => {
          setFileViewOpen(true);
          setViewFileId(fileId);
          setViewFileName(fileName);
        },
        openInNewTab: url => window.open(url, '_blank', 'noopener'),
      }
    );
  };

  const filteredData = useMemo(() => {
    // @ts-ignore
    const typeFilter = filterState['type'];
    // @ts-ignore
    const nameFilter = filterState['name'];
    return typedData
      ?.filter(
        (file: RepositoryFileWithVotes) =>
          (!typeFilter ||
            typeFilter === ALL_FILE_TYPES ||
            (typeFilter && file.type === Number(typeFilter))) &&
          (!nameFilter ||
            (nameFilter && file.name.toLowerCase().includes(nameFilter.toLowerCase())))
      )
      .sort((a: RepositoryFileWithVotes, b: RepositoryFileWithVotes) => {
        let result = 0;

        if (sortProperty === 'name') {
          result = a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
        } else if (sortProperty === 'data') {
          const aMissing = getMissingResourceDataFields(a).length;
          const bMissing = getMissingResourceDataFields(b).length;
          result = aMissing > bMissing ? 1 : aMissing < bMissing ? -1 : 0;
        } else if (sortProperty === 'upvotes') {
          result = (a.upvote_count ?? 0) > (b.upvote_count ?? 0) ? 1 : -1;
        } else if (sortProperty === 'downvotes') {
          result = (a.downvote_count ?? 0) > (b.downvote_count ?? 0) ? 1 : -1;
        } else {
          result = (a.type ?? 0) > (b.type ?? 0) ? 1 : -1;
        }

        return result * sortDirection;
      });
  }, [data, filterState, sortDirection, sortProperty]);

  const handleAddFileClick = () => {
    navigate('/settings/admin/repository-management/add-file');
  };

  const handleAddLinkClick = () => {
    navigate('/settings/admin/repository-management/file-details', {
      state: {
        data: {
          name: '',
          description: '',
          type: FileTypes.URL,
          size: 0,
          object_key: '',
          repository_id: 1,
        },
      },
      replace: true,
    });
  };

  const handlePageChange = (_: any, { activePage: page }: any) => {
    setActivePage(page);
  };

  const paginatedData = (filteredData || typedData)?.slice(
    (activePage - 1) * itemsPerPage,
    activePage * itemsPerPage
  );

  const handleFilterChange = (filterState: any) => {
    setFilterState(filterState);
    setActivePage(1);
    if (filterState.type !== undefined) {
      dispatch(setTypeFilter(filterState.type));
    }
  };

  const handleSortChange = (property: string) => {
    if (property === sortProperty) {
      setSortDirection(sortDirection * -1);
    } else {
      setSortProperty(property);
    }
  };

  return (
    <>
      {props.editable && (
        <>
          <PageHeader
            title="Repository Management"
            divider
            controls={
              <ControlContainer>
                <StyledAddButton
                  label="Add Link"
                  width="auto"
                  color="blue"
                  onClick={props.editable ? handleAddLinkClick : undefined}
                />
                <StyledAddButton
                  label="Upload File"
                  width="auto"
                  onClick={props.editable ? handleAddFileClick : undefined}
                />
              </ControlContainer>
            }
          />
        </>
      )}

      <PageSection padbottom style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Input
          icon="search"
          iconPosition="left"
          placeholder="Search..."
          style={{ width: '30%' }}
          value={pendingFilterState['name']}
          onChange={e => {
            const updatedFilterState = {
              ...pendingFilterState,
              name: e.target.value,
            };
            setPendingFilterState(updatedFilterState);
            handleFilterChange(updatedFilterState);
          }}
        />
        <Select
          placeholder="Filter by Type"
          options={[
            { key: 'all', text: 'All Files', value: ALL_FILE_TYPES },
            { key: 'pdf', text: 'Document', value: FileTypes.PDF.toString() },
            { key: 'mp4', text: 'Video', value: FileTypes.MP4.toString() },
            { key: 'url', text: 'Link', value: FileTypes.URL.toString() },
          ]}
          value={pendingFilterState['type']}
          onChange={(e, { value }) => {
            const updatedFilterState = {
              ...pendingFilterState,
              type: value as string,
            };
            setPendingFilterState(updatedFilterState);
            handleFilterChange(updatedFilterState);
          }}
        />
      </PageSection>

      {paginatedData.length > 0 ? (
        <>
          <PageSection>
            <Table collapsing style={{ margin: 'auto' }}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell
                    width={4}
                    onClick={() => handleSortChange('name')}
                    style={{ cursor: 'pointer', userSelect: 'none', verticalAlign: 'middle', padding: '0.75em 0.5em' }}
                    title="Sort by name"
                  >
                    Name
                    <Icon
                      name="triangle up"
                      style={{ marginLeft: '0.5em', color: 'grey', opacity: sortProperty === 'name' ? 1 : 0 }}
                    />
                  </TableHeaderCell>
                  <TableHeaderCell width={6}>Description</TableHeaderCell>
                  <TableHeaderCell
                    width={1}
                    onClick={() => handleSortChange('data')}
                    style={{ cursor: 'pointer', userSelect: 'none', verticalAlign: 'middle', padding: '0.75em 0.5em' }}
                    title="Sort by data completeness"
                  >
                    Data
                    <Icon
                      name="triangle up"
                      style={{ marginLeft: '0.5em', color: 'grey', opacity: sortProperty === 'data' ? 1 : 0 }}
                    />
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={1}
                    onClick={() => handleSortChange('type')}
                    style={{ cursor: 'pointer', userSelect: 'none', verticalAlign: 'middle', padding: '0.75em 0.5em' }}
                    title="Sort by type"
                  >
                    Type
                    <Icon
                      name="triangle up"
                      style={{ marginLeft: '0.5em', color: 'grey', opacity: sortProperty === 'type' ? 1 : 0 }}
                    />
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={1}
                    onClick={() => handleSortChange('size')}
                    style={{ cursor: 'pointer', userSelect: 'none', verticalAlign: 'middle', padding: '0.75em 0.5em' }}
                    title="Sort by size"
                  >
                    Size
                    <Icon
                      name="triangle up"
                      style={{ marginLeft: '0.5em', color: 'grey', opacity: sortProperty === 'size' ? 1 : 0 }}
                    />
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={1}
                    onClick={() => handleSortChange('upvotes')}
                    style={{ cursor: 'pointer', userSelect: 'none', verticalAlign: 'middle', padding: '0.75em 0.5em' }}
                    title="Sort by upvotes"
                  >
                    <Icon name="thumbs up" />
                    <Icon
                      name="triangle up"
                      style={{ marginLeft: '0.5em', color: 'grey', opacity: sortProperty === 'upvotes' ? 1 : 0 }}
                    />
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={1}
                    onClick={() => handleSortChange('downvotes')}
                    style={{ cursor: 'pointer', userSelect: 'none', verticalAlign: 'middle', padding: '0.75em 0.5em' }}
                    title="Sort by downvotes"
                  >
                    <Icon name="thumbs down" />
                    <Icon
                      name="triangle up"
                      style={{ marginLeft: '0.5em', color: 'grey', opacity: sortProperty === 'downvotes' ? 1 : 0 }}
                    />
                  </TableHeaderCell>
                  <TableHeaderCell width={1}></TableHeaderCell>
                </TableRow>
              </TableHeader>

              <TableBody>
                {paginatedData?.map((file: RepositoryFileWithVotes) => (
                  <TableRow
                    key={file.id}
                    onClick={() => props.onRowClick(file)}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell style={{ verticalAlign: 'top' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <div style={{ marginRight: '0.1em' }}>{getFileTypeIcon(file.type ?? FileTypes.INVALID_FILE_TYPE)}</div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {file.name}
                            {getProcessingStatusIcon(file)}
                          </div>
                          <div
                            style={{ fontSize: '0.85em', color: 'grey', marginTop: '2px' }}
                            title={file.object_key}
                          >
                            {file.object_key.length > 35
                              ? `${file.object_key.slice(0, 35)}...${file.object_key.slice(-4)}`
                              : file.object_key}{' '}
                            (id: {file.id})
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell style={{ verticalAlign: 'top' }}>
                      <div
                        style={{ fontSize: '0.85em', color: 'grey', marginTop: '2px' }}
                        title={file.description ?? ''}
                      >
                        {file?.description && file?.description.length > 200
                          ? `${file.description.slice(0, 200)}...`
                          : file.description}
                      </div>
                    </TableCell>
                    <TableCell style={{ verticalAlign: 'top' }}>{getDataStatusIcon(file)}</TableCell>
                    <TableCell>{getFileLabelFromType(file.type ?? FileTypes.INVALID_FILE_TYPE)}</TableCell>
                    <TableCell>
                      {file.type === FileTypes.URL ? '' : getFileSizeFormatted(file.size ?? 0)}
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: 'center',
                        color: (file.upvote_count ?? 0) > 0 ? 'green' : undefined,
                        fontWeight: (file.upvote_count ?? 0) > 0 ? 600 : 'normal',
                      }}
                    >
                      {file.upvote_count ?? 0}
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: 'center',
                        color: (file.downvote_count ?? 0) > 0 ? 'red' : undefined,
                        fontWeight: (file.downvote_count ?? 0) > 0 ? 600 : 'normal',
                      }}
                    >
                      {file.downvote_count ?? 0}
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', gap: '3px' }}>
                        <Button
                          icon
                          onClick={event => {
                            event.stopPropagation();
                            handleViewClick(file.id, file.type ?? FileTypes.INVALID_FILE_TYPE, file.object_key, file.name);
                          }}
                        >
                          <Icon
                            name={file.type === FileTypes.URL ? 'external alternate' : 'television'}
                          />
                        </Button>

                        {props.editable && (
                          <StyledTrashButton onClick={confirmDelete} id={file.id} title={"Remove Resource"} />
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableHeaderCell colSpan="8" style={{ textAlign: 'right' }}>
                    <div style={{ display: 'inline-block' }}>
                      <Menu pagination>
                        <Pagination
                          totalPages={Math.ceil(
                            (filteredData || (data as any))?.length / itemsPerPage
                          )}
                          activePage={activePage}
                          onPageChange={handlePageChange}
                          boundaryRange={1}
                          size="mini"
                          firstItem={null}
                          lastItem={null}
                        />
                      </Menu>
                    </div>
                  </TableHeaderCell>
                </TableRow>
              </TableFooter>
            </Table>
          </PageSection>
          <FileViewModal
            title={viewFileName}
            fileId={viewFileId.toString()}
            isOpen={isFileViewOpen}
            setModalOpen={setFileViewOpen}
          />
        </>
      ) : (
        <PageSection>
          <div
            style={{
              textAlign: 'center',
              padding: '4rem',
              color: '#888',
              fontSize: '1.2em',
            }}
          >
            <Icon name="folder open" size="huge" style={{ marginBottom: '1rem' }} />
            <p>No resources found</p>
          </div>
        </PageSection>
      )}

      <DeleteConfirmationModal
        isOpen={pendingDeleteFileId !== undefined}
        onCancel={() => {
          setPendingDeleteFileId(undefined);
        }}
        onDelete={props.editable ? () => handleDelete(pendingDeleteFileId) : () => undefined}
      />
    </>
  );
};

export default RepositoryContentList;
